/**
 * Elite Fade Designs - Main Entry Point
 */

import './styles/main.css';
import { initI18n, t } from './i18n/index.js';
import { initHeader } from './components/common/Header/index.js';
import { Footer } from './components/common/Footer/index.js';
import { initFloatingBookBtn } from './components/common/FloatingBookBtn.js';
import { initChatWidget } from './components/common/ChatWidget/ChatWidget.js';
import { isAuthenticated, hasAnyGroup } from './services/auth.service.js';

const config = {
  apiUrl: import.meta.env.VITE_API_URL || 'https://api.elitefadedesigns.com/v1',
  env: import.meta.env.MODE || 'development',
};

const state = {
  isLoading: true,
  header: null,
  footer: null,
  floatingBtn: null,
};

// ---------------------------------------------------------------------------
// Path normalization
// ---------------------------------------------------------------------------
function currentPath() {
  let p = window.location.pathname || '/';
  if (p.length > 1 && p.endsWith('/')) p = p.slice(0, -1);
  return p;
}

// ---------------------------------------------------------------------------
// Admin route map
// ---------------------------------------------------------------------------
const ADMIN_ROUTES = {
  '/admin':              { titleKey: 'admin.pageTitles.overview',      useComponent: 'dashboard' },
  '/admin/bookings':     { titleKey: 'admin.pageTitles.bookings',      useComponent: 'bookings' },
  '/admin/services':     { titleKey: 'admin.pageTitles.services',      useComponent: 'services' },
  '/admin/staff':        { titleKey: 'admin.pageTitles.staff',         useComponent: 'staff' },
  '/admin/schedule':     { titleKey: 'admin.pageTitles.schedule',      useComponent: 'schedule' },
  '/admin/rides':        { titleKey: 'admin.pageTitles.rides',         useComponent: 'rides' },
  '/admin/conversations':{ titleKey: 'admin.pageTitles.conversations', useComponent: 'conversations' },
  '/admin/clients':      { titleKey: 'admin.pageTitles.clients',       useComponent: 'clients' },
};

function placeholderRenderer(titleKey, placeholderKey) {
  return (contentEl) => {
    contentEl.innerHTML = `
      <div class="admin-placeholder">
        <h2 class="admin-placeholder__title">${escapeHtml(t(titleKey))}</h2>
        <p class="admin-placeholder__text">${escapeHtml(t(placeholderKey))}</p>
      </div>
    `;
  };
}

function escapeHtml(s) {
  if (s == null) return '';
  return String(s).replace(/[&<>"']/g, (c) => ({
    '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;',
  }[c]));
}

function isChromelessRoute(path) {
  return (
    path === '/efd-admin' ||
    path === '/signin' ||
    path === '/signup' ||
    path.startsWith('/admin')
  );
}

async function initApp() {
  try {
    initI18n();

    const path = currentPath();
    const chromeless = isChromelessRoute(path);

    if (!chromeless) {
      state.header = initHeader({ dark: isHeroPage() });

      const footerEl = Footer();
      document.body.appendChild(footerEl);
      state.footer = footerEl;

      initFloatingBookBtn();
      initChatWidget();
    }

    await initPage();
    hideLoadingScreen();
    setupEventListeners();

  } catch (error) {
    console.error('[Elite Fade] Failed to initialize app:', error);
    showErrorScreen();
  }
}

function isHeroPage() {
  const heroPages = ['/', '/index.html'];
  return heroPages.includes(currentPath());
}

async function initPage() {
  const path = currentPath();
  const app = document.getElementById('app');
  if (app) app.innerHTML = '';

  if (path.startsWith('/admin')) {
    const ok = await guardAdminRoute();
    if (!ok) return;
    await loadAdminPage(app, path);
    return;
  }

  switch (path) {
    case '/':
    case '/index.html':
      await loadHomePage(app);
      break;
    case '/services':
      await loadServicesPage(app);
      break;
    case '/gallery':
      await loadGalleryPage(app);
      break;
    case '/about':
      await loadAboutPage(app);
      break;
    case '/contact':
      await loadContactPage(app);
      break;
    case '/barbers':
      await loadBarbersPage(app);
      break;
    case '/booking':
    case '/book':
      await loadBookingPage(app);
      break;
    case '/dashboard':
      await loadDashboardPage(app);
      break;
    case '/efd-admin':
      await loadLoginPage(app);
      break;
    case '/signup':
      await loadSignUpPage(app);
      break;
    case '/signin':
      await loadCustomerLoginPage(app);
      break;
    default:
      await load404Page(app);
  }
}

async function loadHomePage(container) {
  const { loadHomePage: render } = await import('./pages/Home/Home.js');
  return render(container);
}

async function loadServicesPage(container) {
  const { loadServicesPage: renderServices } = await import('./pages/Services/Services.js');
  renderServices(container);
}

async function loadGalleryPage(container) {
  const { loadGalleryPage: render } = await import('./pages/Gallery/Gallery.js');
  return render(container);
}

async function loadAboutPage(container) {
  const { loadAboutPage: render } = await import('./pages/About/About.js');
  return render(container);
}

async function loadContactPage(container) {
  const { loadContactPage: render } = await import('./pages/Contact/Contact.js');
  return render(container);
}

async function loadBarbersPage(container) {
  const { loadBarbersPage: render } = await import('./pages/Barbers/Barbers.js');
  return render(container);
}

async function loadBookingPage(container) {
  // Routing decision: Express vs full flow.
  //
  //   - URL `?full=1` always forces the standard 5-step flow (used by
  //     the "Change barber" link inside Express, and the public
  //     "Book Now" buttons from guest entry points if needed).
  //   - Otherwise, if the customer is signed in AND has a preferred
  //     barber, route to ExpressBooking.
  //   - Anything else → standard BookingFlow.
  const params = new URLSearchParams(window.location.search);
  const forceFull = params.get('full') === '1';

  if (!forceFull) {
    const useExpress = await shouldUseExpressBooking();
    if (useExpress) {
      const { ExpressBooking } = await import(
        './pages/booking/ExpressBooking/ExpressBooking.js'
      );
      container.innerHTML = '';
      container.appendChild(ExpressBooking());
      return;
    }
  }

  const { BookingFlow } = await import('./pages/booking/BookingFlow/BookingFlow.js');
  container.innerHTML = '';
  container.appendChild(BookingFlow());
}

/**
 * Decide whether the current visitor qualifies for ExpressBooking.
 * Wrapped in try/catch so any unexpected error in the customer-auth
 * service silently falls back to the full flow rather than breaking
 * the booking page entirely.
 */
async function shouldUseExpressBooking() {
  try {
    const mod = await import('./services/customer-auth.service.js');
    const session = mod.getSession?.();
    // getSession() can return a Promise during refresh — wait for it.
    const resolvedSession = session && typeof session.then === 'function'
      ? await session
      : session;
    if (!resolvedSession || !resolvedSession.idToken) return false;

    const fav = mod.getPreferredBarber?.();
    return !!fav;
  } catch (err) {
    console.warn('[router] Express booking gate failed, using full flow', err);
    return false;
  }
}

async function loadDashboardPage(container) {
  const { loadDashboardPage: render } = await import('./pages/Dashboard/Dashboard.js');
  return render(container);
}

async function loadLoginPage(container) {
  const { loadLoginPage: render } = await import('./pages/auth/Login/index.js');
  return render(container);
}

async function loadSignUpPage(container) {
  const { loadSignUpPage: render } = await import('./pages/auth/SignUp/SignUp.js');
  return render(container);
}

async function loadCustomerLoginPage(container) {
  const { loadCustomerLoginPage: render } = await import('./pages/auth/CustomerLogin/CustomerLogin.js');
  return render(container);
}

async function loadAdminPage(container, path) {
  const route = ADMIN_ROUTES[path];
  const { renderAdminLayout } = await import('./pages/admin/AdminLayout/index.js');

  if (!route) {
    await renderAdminLayout(container, {
      activePath: '/admin',
      pageTitle: t('admin.pageTitles.notFound'),
      renderContent: (el) => {
        el.innerHTML = `
          <div class="admin-placeholder">
            <h2 class="admin-placeholder__title">404</h2>
            <p class="admin-placeholder__text">${escapeHtml(t('admin.placeholders.notFound'))}</p>
          </div>
        `;
      },
    });
    return;
  }

  let renderContent;
  if (route.useComponent === 'dashboard') {
    const mod = await import('./pages/admin/Dashboard/Dashboard.js');
    renderContent = mod.renderDashboard;
  } else if (route.useComponent === 'bookings') {
    const mod = await import('./pages/admin/Bookings/Bookings.js');
    renderContent = mod.renderBookings;
  } else if (route.useComponent === 'services') {
    const mod = await import('./pages/admin/Services/Services.js');
    renderContent = mod.renderServicesAdmin;
  } else if (route.useComponent === 'staff') {
    const mod = await import('./pages/admin/Staff/Staff.js');
    renderContent = mod.renderStaffAdmin;
  } else if (route.useComponent === 'schedule') {
    const mod = await import('./pages/admin/Schedule/Schedule.js');
    renderContent = mod.renderSchedule;
  } else if (route.useComponent === 'rides') {
    const mod = await import('./pages/admin/Rides/Rides.js');
    renderContent = mod.renderRides;
  } else if (route.useComponent === 'clients') {
    const mod = await import('./pages/admin/Clients/Clients.js');
    renderContent = mod.renderClientsAdmin;
  } else if (route.useComponent === 'conversations') {
    const mod = await import('./pages/admin/Conversations/Conversations.js');
    renderContent = mod.renderConversationsAdmin;
  } else {
    renderContent = placeholderRenderer(route.titleKey, route.placeholderKey);
  }

  await renderAdminLayout(container, {
    activePath: path,
    pageTitle: t(route.titleKey),
    renderContent,
  });
}

async function load404Page(container) {
  container.innerHTML = `
    <div class="page">
      <section class="section" style="padding-top: 8rem;">
        <div class="container text-center">
          <h1 class="text-display mb-4">404</h1>
          <p class="text-body-lg text-muted mb-8">Page not found</p>
          <a href="/" data-link class="btn btn-primary">Go Home</a>
        </div>
      </section>
    </div>
  `;
  attachSPALinks(container);
}

async function guardAdminRoute() {
  const authed = await isAuthenticated();
  if (!authed) {
    const target = currentPath() + window.location.search;
    const url = `/efd-admin?redirect=${encodeURIComponent(target)}`;
    window.history.replaceState({}, '', url);
    window.dispatchEvent(new PopStateEvent('popstate'));
    return false;
  }

  const ok = await hasAnyGroup(['Admin', 'Staff', 'Dispatcher']);
  if (!ok) {
    window.history.replaceState({}, '', '/');
    window.dispatchEvent(new PopStateEvent('popstate'));
    return false;
  }

  return true;
}

function attachSPALinks(container) {
  container.querySelectorAll('[data-link]').forEach(link => {
    link.addEventListener('click', e => {
      e.preventDefault();
      const path = link.getAttribute('href');
      window.history.pushState({}, '', path);
      window.dispatchEvent(new PopStateEvent('popstate'));
    });
  });
}

function hideLoadingScreen() {
  const loadingScreen = document.getElementById('loading-screen');
  if (loadingScreen) {
    loadingScreen.classList.add('hidden');
    state.isLoading = false;
  }
}

function showErrorScreen() {
  const app = document.getElementById('app');
  if (app) {
    app.innerHTML = `
      <div class="page">
        <section class="section" style="padding-top: 8rem;">
          <div class="container text-center">
            <h1 class="text-h1 mb-4">Something went wrong</h1>
            <p class="text-muted mb-6">Please refresh the page and try again.</p>
            <button class="btn btn-primary" onclick="window.location.reload()">Refresh Page</button>
          </div>
        </section>
      </div>
    `;
  }
}

function setupEventListeners() {
  window.addEventListener('popstate', async () => {
    syncPublicChrome();
    await initPage();
    if (isHeroPage()) setupHeroScrollEffect();
  });

  window.addEventListener('languageChange', () => {
    if (state.header) state.header.render();
    initPage();
  });

  if (isHeroPage()) {
    setupHeroScrollEffect();
  }
}

function syncPublicChrome() {
  const chromeless = isChromelessRoute(currentPath());
  const headerEl = document.querySelector('.header');
  const footerEl = state.footer;

  if (chromeless) {
    if (headerEl) headerEl.style.display = 'none';
    if (footerEl) footerEl.style.display = 'none';
    document.querySelectorAll('.floating-book-btn').forEach((b) => (b.style.display = 'none'));
    document.querySelectorAll('.efd-chat').forEach((b) => (b.style.display = 'none'));
  } else {
    if (headerEl) headerEl.style.display = '';
    if (footerEl) footerEl.style.display = '';
    document.querySelectorAll('.floating-book-btn').forEach((b) => (b.style.display = ''));
    document.querySelectorAll('.efd-chat').forEach((b) => (b.style.display = ''));

    if (!headerEl) {
      state.header = initHeader({ dark: isHeroPage() });
    }
    if (!footerEl) {
      const f = Footer();
      document.body.appendChild(f);
      state.footer = f;
    }
    if (!document.querySelector('.efd-chat')) {
      initChatWidget();
    }
  }
}

function setupHeroScrollEffect() {
  const hero = document.querySelector('.hero');
  const header = document.querySelector('.header');
  if (!hero || !header) return;

  const heroHeight = hero.offsetHeight;

  window.addEventListener('scroll', () => {
    const scrollY = window.scrollY;
    const progress = Math.min(scrollY / (heroHeight * 0.6), 1);
    hero.style.opacity = 1 - progress;
    hero.style.transform = `scale(${1 - progress * 0.04})`;
    if (scrollY > 40) {
      header.classList.add('header--scrolled');
    } else {
      header.classList.remove('header--scrolled');
    }
  }, { passive: true });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initApp);
} else {
  initApp();
}

window.__EliteFade = { config, state };
